import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const LS_TOKEN = "mb_token";
const LS_USER = "mb_user";
const LS_SUCURSALES = "mb_sucursales";
const LS_SELECTED = "mb_selected_sucursal";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  const [selectedSucursalId, setSelectedSucursalIdState] = useState(localStorage.getItem(LS_SELECTED) || null);

  const _bootstrapFromLogin = (data) => {
    localStorage.setItem(LS_TOKEN, data.token);
    localStorage.setItem(LS_USER, JSON.stringify(data.user));
    const sucs = data.sucursales || [];
    localStorage.setItem(LS_SUCURSALES, JSON.stringify(sucs));
    setUser(data.user);
    setSucursales(sucs);
    // Sucursal por defecto: la última seleccionada si está en la lista, si no la primera disponible
    let preferred = localStorage.getItem(LS_SELECTED);
    if (!preferred || !sucs.some((s) => s.id === preferred)) {
      preferred = data.user?.sucursal_id || (sucs[0]?.id ?? null);
    }
    if (preferred) {
      localStorage.setItem(LS_SELECTED, preferred);
      setSelectedSucursalIdState(preferred);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(LS_TOKEN);
    const stored = localStorage.getItem(LS_USER);
    if (!token || !stored) { setLoading(false); return; }
    try { setUser(JSON.parse(stored)); } catch {}
    const sucsRaw = localStorage.getItem(LS_SUCURSALES);
    if (sucsRaw) {
      try { setSucursales(JSON.parse(sucsRaw)); } catch {}
    }
    // Validar sesión y refrescar sucursales
    Promise.all([
      api.get("/auth/me"),
      api.get("/auth/mis-sucursales").catch(() => ({ data: [] })),
    ]).then(([me, ms]) => {
      setUser(me.data);
      localStorage.setItem(LS_USER, JSON.stringify(me.data));
      setSucursales(ms.data || []);
      localStorage.setItem(LS_SUCURSALES, JSON.stringify(ms.data || []));
      // Asegura una sucursal seleccionada válida
      let sel = localStorage.getItem(LS_SELECTED);
      const valid = (ms.data || []).some((s) => s.id === sel);
      if (!valid) {
        sel = me.data?.sucursal_id || (ms.data?.[0]?.id ?? null);
        if (sel) localStorage.setItem(LS_SELECTED, sel);
        setSelectedSucursalIdState(sel);
      }
    }).catch(() => {
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_USER);
      localStorage.removeItem(LS_SUCURSALES);
      localStorage.removeItem(LS_SELECTED);
      setUser(null);
      setSucursales([]);
      setSelectedSucursalIdState(null);
    }).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      _bootstrapFromLogin(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_SUCURSALES);
    localStorage.removeItem(LS_SELECTED);
    setUser(null);
    setSucursales([]);
    setSelectedSucursalIdState(null);
  };

  const setSelectedSucursalId = useCallback((id) => {
    if (!id) return;
    localStorage.setItem(LS_SELECTED, id);
    setSelectedSucursalIdState(id);
    // Disparar refresco a la pantalla actual
    window.dispatchEvent(new CustomEvent("sucursal-changed", { detail: { id } }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, sucursales, selectedSucursalId, setSelectedSucursalId }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
